import { ethers } from 'ethers';
import { CANBORSA_CONTRACTS, CANBORSA_TOKENS, CANBORSA_CHAIN_ID } from './canborsaConstants';
import { getInjectedProvider } from '../../utils/injectedProvider';

// ─────────────────────────────────────────────────────────────────────────────
// Canborsa DEX — utility functions
//
// All functions marked TODO throw NotImplementedError until contract addresses
// and ABI are discovered via devtools inspection on canborsa.com.
// Functions with real implementations (balance, approval) are safe to use now.
// ─────────────────────────────────────────────────────────────────────────────

class NotImplementedError extends Error {
  constructor(fn) {
    super(`[CanborsaUtils] ${fn}: contract address unknown — complete devtools inspection first`);
    this.name = 'NotImplementedError';
  }
}

// ── ERC-20 minimal ABI (works for balance + approval of any collateral token) ─
export const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
];

// ── Collateral balance ────────────────────────────────────────────────────────
export async function getCollateralBalance(provider, address, symbol = 'USDC') {
  const token = CANBORSA_TOKENS[symbol];
  if (!token) throw new Error(`Unknown collateral symbol: ${symbol}`);
  const contract = new ethers.Contract(token.address, ERC20_ABI, provider);
  const raw = await contract.balanceOf(address);
  return parseFloat(ethers.utils.formatUnits(raw, token.decimals));
}

// ── Collateral approval ───────────────────────────────────────────────────────
// Approves CANBORSA_CONTRACTS.Router to spend `amountRaw` of `symbol`.
// TODO: Confirm Router is the correct spender (may be Vault or PositionManager).
export async function ensureCollateralApproved(signer, amountRaw, symbol = 'USDC') {
  if (!CANBORSA_CONTRACTS.Router) throw new NotImplementedError('ensureCollateralApproved');
  const token = CANBORSA_TOKENS[symbol];
  const contract = new ethers.Contract(token.address, ERC20_ABI, signer);
  const allowance = await contract.allowance(await signer.getAddress(), CANBORSA_CONTRACTS.Router);
  if (allowance.lt(amountRaw)) {
    const tx = await contract.approve(CANBORSA_CONTRACTS.Router, amountRaw);
    await tx.wait(1);
  }
}

// ── Execution fee ─────────────────────────────────────────────────────────────
// TODO: Read from contract — may not need an execution fee (not a keeper model).
// Return 0 until we know the fee structure.
export async function fetchCanborsaExecutionFee(provider) { // eslint-disable-line no-unused-vars
  if (!CANBORSA_CONTRACTS.Router) return ethers.BigNumber.from(0);
  // TODO: call contract.minExecutionFee() or equivalent
  return ethers.BigNumber.from(0);
}

// ── Open position (market order) ──────────────────────────────────────────────
// TODO: Replace with actual ABI call once contract is known.
// Likely params: market, isLong, collateralToken, collateralAmount, leverage
export async function openCanborsaPosition({ // eslint-disable-line no-unused-vars
  signer,
  marketToken,    // TODO: Canborsa market identifier (address or bytes32 key)
  collateralToken,
  collateralAmountRaw,
  isLong,
  leverage,
  executionFeeWei,
}) {
  if (!CANBORSA_CONTRACTS.Router) throw new NotImplementedError('openCanborsaPosition');
  // TODO: build tx with discovered ABI
  // const router = new ethers.Contract(CANBORSA_CONTRACTS.Router, ROUTER_ABI, signer);
  // return router.openPosition(marketToken, isLong, collateralToken, collateralAmountRaw, leverage, { value: executionFeeWei });
  throw new NotImplementedError('openCanborsaPosition');
}

// ── Close position ────────────────────────────────────────────────────────────
export async function closeCanborsaPosition({ // eslint-disable-line no-unused-vars
  signer,
  positionKey,    // TODO: position identifier from fetchOpenPositions
  executionFeeWei,
}) {
  if (!CANBORSA_CONTRACTS.Router) throw new NotImplementedError('closeCanborsaPosition');
  throw new NotImplementedError('closeCanborsaPosition');
}

// ── Fetch open positions ──────────────────────────────────────────────────────
// Returns array of normalized position objects matching GmxPanel's shape:
// { key, marketKey, isLong, sizeUsd, collateralUsd, entryPrice, leverage, marketToken, collateralToken }
export async function fetchCanborsaPositions(provider, address) { // eslint-disable-line no-unused-vars
  if (!CANBORSA_CONTRACTS.PositionManager) {
    // Silently return empty until contracts are wired — avoids noisy errors during dev
    return [];
  }
  // TODO: call PositionManager.getPositions(address) or equivalent subgraph query
  throw new NotImplementedError('fetchCanborsaPositions');
}

// ── On-chain oracle price ─────────────────────────────────────────────────────
// Returns null until oracle address is known — caller falls back to CoinGecko price.
export async function fetchCanborsaOraclePrice(provider, marketToken) { // eslint-disable-line no-unused-vars
  if (!CANBORSA_CONTRACTS.Oracle) return null;
  // TODO: call Oracle.getPrice(marketToken) or similar
  return null;
}

// ── Chain switch ──────────────────────────────────────────────────────────────
export async function switchToArbitrum() {
  const injected = getInjectedProvider();
  if (!injected) throw new Error('No wallet provider available');
  await injected.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: `0x${CANBORSA_CHAIN_ID.toString(16)}` }],
  });
}
